import styled, { keyframes } from 'styled-components';
import ndsTokens from 'nicollet-tokens';
import { ndsFontReset, ndsFormStyles } from '@nicollet/common';
import { Label } from '../Label/index.js';
import { Microcopy } from '../Microcopy/index.js';

const CHECKBOX_SIZE = 20;
const checkmarkSize = CHECKBOX_SIZE / 2;
const CheckboxOuterWrap = styled.div`
  ${ndsFontReset()};
  box-sizing: border-box;
  position: relative;
  line-height: 0;
`;
const checkmarkExpand = keyframes`
  0% {
    transform: scale(0.4) rotate(20deg);
  }

  70% {
    transform: scale(1.2) rotate(33deg);
  }

  100% {
    transform: scale(1) rotate(33deg);
  }
`;
const checkboxFocusStyles = `
  outline: ${ndsTokens.colors.text.formLabel} dashed 1px;
  outline-offset: 2px;
  -moz-outline-radius: 4px;
`;
const CheckboxInput = styled.input`
  border: 0;
  cursor: pointer;
  height: ${CHECKBOX_SIZE}px;
  left: 0;
  margin: 0;
  opacity: 0;
  padding: 0;
  position: absolute;
  top: 0;
  width: ${CHECKBOX_SIZE}px;
`;
const CheckboxMicrocopy = styled(Microcopy)`
  padding-left: ${ndsTokens.space.generic.x7};
  padding-top: 0;
  width: 100%;
`;
const CheckboxWrap = styled.label`
  cursor: pointer;
  display: inline-flex;
  flex-wrap: wrap;
  position: relative;

  ${props => props.hasFocus && checkboxFocusStyles};
  ${CheckboxInput}:focus + & {
    ${checkboxFocusStyles};
  }
`; // Checkbox Label - the text to the right of the checkbox

const checkboxLabelHoverStyles = `
  color: ${ndsTokens.colors.palette.black};
`;
const checkboxLabelActiveStyles = `
  color: ${ndsTokens.colors.palette.gray.darkest};
`;
const CheckboxLabel = styled(Label)`
  padding-left: ${ndsTokens.space.generic.x7};

  ${props => props.hidden && `
    padding-left: 0;
  `};

  ${props => props.hasHover && checkboxLabelHoverStyles};
  ${CheckboxWrap}:hover & {
    ${checkboxLabelHoverStyles};
  }

  ${props => props.hasActive && checkboxLabelActiveStyles};
  ${CheckboxInput}:active + ${CheckboxWrap} & {
    ${checkboxLabelActiveStyles};
  }

  ${CheckboxInput}:disabled + ${CheckboxWrap} & {
    color: ${ndsTokens.colors.text.onLight.secondary};
    background: transparent;
  }
`; // Checkbox Visual - the actual "box" you see

const checkboxVisualHoverStyles = `
  border-color: ${ndsTokens.colors.palette.gray.gray40};
`;
const checkboxVisualCheckedHoverStyles = `
  background-color: ${ndsTokens.colors.palette.green.default};
  border-color: ${ndsTokens.colors.palette.green.default};
`;
const checkboxVisualActiveStyles = `
  background-color: ${ndsTokens.colors.palette.gray.light};
  border-color: ${ndsTokens.colors.border.control.input};
`;
const checkboxVisualCheckedActiveStyles = `
  background-color: ${ndsTokens.colors.palette.green.default};
  border-color: ${ndsTokens.colors.palette.green.default};
`; // If label is hidden, checkbox should be statically positioned so focus ring works correctly

const CheckboxVisual = styled.div`
  background-color: ${ndsTokens.colors.background.default};
  border: 1px solid ${ndsTokens.colors.border.interactive.secondary.default};
  border-radius: 3px;
  box-sizing: border-box;
  height: ${CHECKBOX_SIZE}px;
  left: 0;
  position: ${({
  hiddenLabel
}) => hiddenLabel ? 'static' : 'absolute'};
  top: 1px;
  width: ${CHECKBOX_SIZE}px;

  ${({
  valid
}) => valid === false && `
      background-color: ${ndsFormStyles.colors.alert.warning.background};
      border-color: ${ndsFormStyles.colors.alert.warning.text};
    `};

  ${({
  valid
}) => valid === true && `
      border-color: ${ndsTokens.colors.alert.success};
    `};

  ${({
  hasHover
}) => hasHover && checkboxVisualHoverStyles};
  ${CheckboxWrap}:hover & {
    ${checkboxVisualHoverStyles};
  }

  /* Checked */
  ${CheckboxInput}:checked:not(:disabled) + ${CheckboxWrap} & {
    background-color: ${ndsTokens.colors.palette.green.light};
    border-color: ${ndsTokens.colors.palette.green.light};
    ${props => props.hasHover && checkboxVisualCheckedHoverStyles};
    ${props => props.hasActive && checkboxVisualCheckedActiveStyles};

    &::after {
      box-sizing: border-box;
      position: absolute;
      content: '';
      top: 3px;
      left: 7px;
      width: ${checkmarkSize / 2}px;
      height: ${checkmarkSize}px;
      border-color: white;
      border-style: solid;
      border-top: 0;
      border-right-width: ${checkmarkSize / 5}px;
      border-bottom-width: ${checkmarkSize / 5}px;
      border-left: 0;
      transform: rotate(33deg);
      animation: ${checkmarkExpand} 250ms ease-in-out forwards;
    }
  }

  /* Checked, hover */
  ${CheckboxInput}:checked:hover:not(:disabled) + ${CheckboxWrap} & {
    ${checkboxVisualCheckedHoverStyles};
  }

  /* Checked, active */
  ${CheckboxInput}:checked:active:not(:disabled) + ${CheckboxWrap} & {
    ${checkboxVisualCheckedActiveStyles};
  }

  ${({
  hasActive
}) => hasActive && checkboxVisualActiveStyles};
  ${CheckboxInput}:active + ${CheckboxWrap} & {
    ${checkboxVisualActiveStyles};
  }

  ${CheckboxInput}:disabled + ${CheckboxWrap} & {
    &::after {
      box-sizing: border-box;
      position: absolute;
      content: '';
      top: 0;
      left: 0;
      bottom: 0;
      right: 0;
      background: linear-gradient(
        315deg,
        transparent,
        transparent 47%,
        ${ndsTokens.colors.palette.gray.medium} 47%,
        ${ndsTokens.colors.palette.gray.medium} 53%,
        transparent 53%
      );
    }
  }

  ${CheckboxInput}:disabled + ${CheckboxWrap} &,
  ${CheckboxInput}:disabled:checked + ${CheckboxWrap} & {
    background-color: ${ndsTokens.colors.background.default};
    border-color: ${ndsTokens.colors.palette.gray.medium};
    cursor: not-allowed;
    pointer-events: none;
  }
`;

export { CheckboxInput, CheckboxLabel, CheckboxMicrocopy, CheckboxOuterWrap, CheckboxVisual, CheckboxWrap, checkboxLabelActiveStyles, checkboxLabelHoverStyles, checkmarkSize };
